import { defineStore } from 'pinia'
import { ref, watch, onMounted } from 'vue'
import { useNuxtApp } from '#app'
import { useSetSafeStorage } from '~/composables/useSetSafeStorage.js'

const VALID_ORDER_MODES = ['view', 'view_only', 'view_and_order']
const DEFAULT_ORDER_MODE = 'view_only'

export const useRestaurantViewModeStore = defineStore('restaurantViewMode', () => {
  const { $axios } = useNuxtApp()
  const restaurantStore = useRestaurantStore()
  const mode = ref(null)
  const originalMode = ref(null)
  const slug = ref(null)

  const normalizeMode = (value) => {
    const normalized = typeof value === 'string' ? value.trim() : ''
    return VALID_ORDER_MODES.includes(normalized) ? normalized : null
  }

  const normalizeSlug = (value) => {
    const normalized = typeof value === 'string' ? value.trim() : ''
    return normalized ? normalized.replace(/[^a-zA-Z0-9_-]/g, '_') : ''
  }

  const currentSlug = (allowStorageFallback = false) => {
    slug.value = restaurantStore.slug || slug.value || (allowStorageFallback ? useSafeStorage('_slug') : null) || null
    return normalizeSlug(slug.value)
  }

  const storageKey = (key, allowStorageFallback = false) => {
    const scopedSlug = currentSlug(allowStorageFallback)
    return scopedSlug ? `${key}_${scopedSlug}` : null
  }

  const readScopedMode = (key, allowStorageFallback = false) => {
    const keyName = storageKey(key, allowStorageFallback)
    return keyName ? normalizeMode(useSafeStorage(keyName)) : null
  }

  const persistScopedMode = (key, value) => {
    const normalized = normalizeMode(value)
    const keyName = storageKey(key)
    if (!normalized || !keyName) return

    useSetSafeStorage(keyName, normalized)
  }

  onMounted(() => {
    slug.value = restaurantStore.slug || useSafeStorage('_slug') || null;
    originalMode.value = readScopedMode('original_order_mode', true);
    mode.value = originalMode.value || readScopedMode('restaurantViewMode', true) || DEFAULT_ORDER_MODE;

    if (process.client) {
      localStorage.removeItem('original_order_mode')
    }
  })

  const setOriginalMode = (newMode) => {
    const normalized = normalizeMode(newMode)
    if (!normalized) return

    originalMode.value = normalized
    mode.value = normalized
    persistScopedMode('original_order_mode', normalized)
    persistScopedMode('restaurantViewMode', normalized)

    if (process.client) {
      localStorage.removeItem('original_order_mode')
    }
  }

  const setSlug = (newSlug) => {
    slug.value = newSlug || null

    if (originalMode.value) {
      persistScopedMode('original_order_mode', originalMode.value)
    }

    if (mode.value) {
      persistScopedMode('restaurantViewMode', mode.value)
    }
  }

  const setModeFromApi = (newMode) => {
    setOriginalMode(normalizeMode(newMode) || DEFAULT_ORDER_MODE)
  }

  const setTemporaryViewOnly = () => {
    mode.value = 'view_only'
  }

  const restoreOriginalMode = () => {
    const restored = normalizeMode(originalMode.value) || readScopedMode('original_order_mode') || DEFAULT_ORDER_MODE
    mode.value = restored
    return restored
  }

  async function getViewMode() {
    slug.value = restaurantStore.slug || useSafeStorage('_slug')
    
    if (!slug.value) return
    try {
      const response = await $axios.get(`restaurant/get-mode-by-slug/${slug.value}`)
      setModeFromApi(response.data.mode)
    } catch (error) {
      console.error("Error fetching view mode:", error)
    }
  }
  watch(mode, (newMode) => {
    const normalized = normalizeMode(newMode)
    if (process.client && normalized) {
      persistScopedMode('restaurantViewMode', normalized)
    }
  })
  watch(originalMode, (newVal) => {
    const normalized = normalizeMode(newVal)
    if (process.client && normalized) {
      persistScopedMode('original_order_mode', normalized)
      localStorage.removeItem('original_order_mode')
    }
  })
  
  return {
    mode,
    slug,
    originalMode,
    getViewMode,
    setSlug,
    setOriginalMode,
    setModeFromApi,
    setTemporaryViewOnly,
    restoreOriginalMode,
  }
})
